import React, { Component } from "react";
import bgn from "../images/bgn.webp";
import eur from "../images/eur.webp";
import usd from "../images/usd.webp";
import gbp from "../images/gbp.webp";
import { type LucideIcon } from "lucide-react";
import { classNames } from "primereact/utils";
import LoadingDots from "./ui/LoadingDots";

interface HoveredWidgetProps {
    isLoading?: boolean | null;
    title: string;
    subHeading?: string | null;
    description?: string | null;
    arrowIcon?: LucideIcon | null;
    icon: string | LucideIcon;
    textClass: string;
    onClick?: () => void | null;
    small?: boolean | null;
    walletsWidget?: boolean;
}

const colorMap: Record<string, { text: string; bg: string; iconBg: string }> = {
    success: {
        text: "text-emerald-600 dark:text-emerald-400",
        bg: "bg-emerald-50 dark:bg-emerald-900/20",
        iconBg: "bg-emerald-100 dark:bg-emerald-900/30",
    },
    info: {
        text: "text-blue-600 dark:text-blue-400",
        bg: "bg-blue-50 dark:bg-blue-900/20",
        iconBg: "bg-blue-100 dark:bg-blue-900/30",
    },
    danger: {
        text: "text-red-600 dark:text-red-400",
        bg: "bg-red-50 dark:bg-red-900/20",
        iconBg: "bg-red-100 dark:bg-red-900/30",
    },
    warning: {
        text: "text-amber-600 dark:text-amber-400",
        bg: "bg-amber-50 dark:bg-amber-900/20",
        iconBg: "bg-amber-100 dark:bg-amber-900/30",
    },
};

export class HoveredWidget extends Component<HoveredWidgetProps, Record<string, never>> {
    curenciesMap = new Map<string, string>([
        ["bgn", bgn],
        ["eur", eur],
        ["usd", usd],
        ["gbp", gbp],
    ]);

    renderFlag = () => {
        const colors = colorMap[this.props.textClass] || colorMap.info;
        const iconName = typeof this.props.icon === "string" ? ((this.props.icon as string) ?? "").toLowerCase() : "";
        if (this.curenciesMap.has(iconName)) {
            return (
                <img
                    role="presentation"
                    alt=""
                    src={this.curenciesMap.get(iconName)}
                    className="w-full rounded-full h-[50px] border border-neutral-200 dark:border-neutral-700 object-contain"
                />
            );
        }
        if (typeof this.props.icon === "string") {
            return <i className={classNames(this.props.icon, colors.text)} />;
        }
        const IconComponent = this.props.icon;
        return <IconComponent size={28} strokeWidth={1.75} className={colors.text} />;
    };

    render() {
        const colors = colorMap[this.props.textClass] || colorMap.info;

        return (
            <div
                className={classNames(
                    "bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm p-5 text-center transition-all duration-200",
                    {
                        "hover:shadow-md hover:-translate-y-0.5 cursor-pointer": !this.props.walletsWidget,
                    },
                )}
                onClick={() => this.props.onClick && this.props.onClick()}
            >
                <div className={classNames("w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3", colors.iconBg)}>
                    {this.renderFlag()}
                </div>
                {this.props.isLoading == true ? (
                    <LoadingDots className="py-2" />
                ) : (
                    <div className={classNames(
                        "font-bold text-neutral-900 dark:text-neutral-100",
                        this.props.small ? "text-xl" : "text-2xl",
                    )}>
                        {this.props.title}
                    </div>
                )}
                <div className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{this.props.subHeading}</div>
                {this.props.description && (
                    <div className={classNames("text-xs mt-2 flex items-center justify-center gap-1", colors.text)}>
                        {this.props.arrowIcon && (() => {
                            const ArrowIcon = this.props.arrowIcon!;
                            return <ArrowIcon size={12} strokeWidth={2} />;
                        })()}
                        <span>{this.props.description}</span>
                    </div>
                )}
            </div>
        );
    }
}
